import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { toast } from 'react-hot-toast';

// Redux
import { setOrderDataAction } from '../../redux/Actions/CreateOrderActions';
import { getDeliveryTimeApi } from '../../redux/Reducers/CreateOrderReducer';

// Utils
import { dateFn, utcTimeToLocalTime, getTimeZoneAbbr, getDifference } from '../../utils/dateTimeFormatter';
import { amountCheckFn, formatAndSortStartTimes, timeIdGenarate } from '../../utils/commonUtil';

// Components
import Loader from '../../components/Loader';

// Images
import bolt from '../../../src/icons/power.svg';

export default function StepDelivery({ onContinue }) {
  const dispatch = useDispatch();
  const { orderType, orderProvider, orderTimeSlots, selectedServices, orderServiceAddress } = useSelector(
    (s) => s.createNewOrder
  );
  const [loading, setLoading] = useState(false);
  const [deliverySchedules, setDeliverySchedules] = useState({});
  const [deliveryDates, setDeliveryDates] = useState([]);
  const [deliveryTimes, setDeliveryTimes] = useState([]);
  const [localSlots, setLocalSlots] = useState(orderTimeSlots || {});

  useEffect(() => {
    setLocalSlots(orderTimeSlots || {});
  }, [orderTimeSlots]);

  // Fetch delivery times
  useEffect(() => {
    const pickupDate = orderType === 'oneTime' ? localSlots?.pickup_date : localSlots?.first_pickup_date;
    if (!pickupDate || !localSlots?.pickup_start_time) return;
    setLoading(true);
    const payload = {
      items: selectedServices?.map((i) => ({ service_id: i.service_id, quantity: i.quantity })),
      pickup_date: pickupDate,
      pickup_start_time: localSlots.pickup_start_time,
      type: orderType === 'oneTime' ? 'ORDER' : 'SUBSCRIPTION',
    };
    getDeliveryTimeApi(
      orderProvider?.id || 'null',
      orderServiceAddress?.id,
      payload,
      orderType === 'oneTime' ? 'ORDER' : 'subscription'
    )
      .then((res) => {
        setLoading(false);
        if (res?.status === 200 || res?.status === 201) {
          const data = formatAndSortStartTimes(res?.data?.data || {}, orderServiceAddress?.time_zone);
          setDeliverySchedules(data);
          // Generate dates
          const dates = Object.keys(data).map((d) => ({
            id: dateFn('YYYYMMDD', d),
            value: dateFn('type3', d),
            dateYYYYMMDD: dateFn('YYYY-MM-DD', d),
            dateWeekday: dateFn('type7', d),
            dateMonthDate: dateFn('type8', d),
            daysAfter: getDifference(pickupDate, d),
          }));
          setDeliveryDates(dates);
          // Default to first non-rush slot
          let defaultSlot = Object.entries(data)
            .flatMap(([date, slots]) => slots.map((s) => ({ date, ...s })))
            .find((s) => !s.is_rush);
          if (!defaultSlot)
            defaultSlot = Object.entries(data).flatMap(([date, slots]) => slots.map((s) => ({ date, ...s })))[0];
          if (defaultSlot) {
            setLocalSlots((prev) => ({
              ...prev,
              dropoff_date: defaultSlot.date,
              dropoff_start_time: defaultSlot.start_time,
              dropoff_end_time: defaultSlot.end_time,
              selected_dropoff_time_id: timeIdGenarate(defaultSlot.date, defaultSlot.start_time),
            }));
            dispatch(setOrderDataAction({ orderIsRush: defaultSlot }));
          }
        } else {
          toast.error(res?.message || 'Something went wrong');
        }
      })
      .catch(() => setLoading(false));
  }, [localSlots?.pickup_start_time]);

  // Generate time slots for selected date
  useEffect(() => {
    if (!localSlots?.dropoff_date || isEmpty(deliverySchedules)) return;
    const tz = orderServiceAddress?.time_zone;
    const date = localSlots.dropoff_date;
    const times = (deliverySchedules[date] || []).map((t, i) => ({
      id: timeIdGenarate(date, t.start_time),
      value: `${utcTimeToLocalTime(`${date}T${t.start_time}`, tz)} - ${utcTimeToLocalTime(
        `${date}T${t.end_time}`,
        tz
      )}`,
      default: { start_time: t.start_time, end_time: t.end_time },
      priority_fee: !isNaN(t.priority_fee) && t.priority_fee >= 0 && amountCheckFn(t.priority_fee),
      is_rush: t.is_rush,
    }));
    setDeliveryTimes(times);
  }, [deliverySchedules, localSlots?.dropoff_date]);

  // Sync to redux
  useEffect(() => {
    if (!isEmpty(localSlots)) dispatch(setOrderDataAction({ orderTimeSlots: localSlots }));
  }, [localSlots]);

  const tz = getTimeZoneAbbr(
    `${localSlots?.pickup_date || localSlots?.first_pickup_date}T${localSlots?.pickup_start_time}`,
    orderServiceAddress?.time_zone
  );
  const hasValid = !!(localSlots?.dropoff_date && localSlots?.dropoff_start_time && localSlots?.dropoff_end_time);

  return (
    <div>
      {loading ? (
        <Loader text='Fetching delivery times...' classes='h-32' />
      ) : (
        <div>
          <p className='mb-3 text-center text-xs font-semibold text-slate-600'>
            Select Delivery Date & Time {tz && `(${tz})`}
          </p>
          <div className='grid gap-3 sm:grid-cols-2'>
            {/* Dates */}
            <div className='space-y-1'>
              {deliveryDates.map((d) => (
                <button
                  key={d.id}
                  type='button'
                  className={`w-full rounded-lg border-2 px-3 py-2 text-left text-sm font-medium transition ${
                    d.dateYYYYMMDD === localSlots?.dropoff_date
                      ? 'border-client-300 bg-blue-50'
                      : 'border-slate-100 bg-slate-50 hover:border-slate-300'
                  }`}
                  onClick={() => {
                    const firstTime = deliverySchedules[d.dateYYYYMMDD]?.[0];
                    setLocalSlots((prev) => ({
                      ...prev,
                      dropoff_date: d.dateYYYYMMDD,
                      dropoff_start_time: firstTime?.start_time,
                      dropoff_end_time: firstTime?.end_time,
                      selected_dropoff_time_id: timeIdGenarate(d.dateYYYYMMDD, firstTime?.start_time),
                      daysAfter: d.daysAfter,
                    }));
                    if (firstTime) dispatch(setOrderDataAction({ orderIsRush: firstTime }));
                  }}
                >
                  {d.value}
                </button>
              ))}
            </div>
            {/* Times */}
            <div className='space-y-1'>
              {deliveryTimes.map((t) => (
                <button
                  key={t.id}
                  type='button'
                  className={`flex w-full items-center gap-2 rounded-lg border-2 px-3 py-2 text-left text-sm font-medium transition ${
                    t.id === localSlots?.selected_dropoff_time_id
                      ? 'border-client-300 bg-blue-50'
                      : 'border-slate-100 bg-slate-50 hover:border-slate-300'
                  }`}
                  onClick={() => {
                    setLocalSlots((prev) => ({
                      ...prev,
                      dropoff_start_time: t.default.start_time,
                      dropoff_end_time: t.default.end_time,
                      selected_dropoff_time_id: t.id,
                    }));
                    dispatch(setOrderDataAction({ orderIsRush: t }));
                  }}
                >
                  <span className='flex-1'>{t.value}</span>
                  {t.is_rush && (
                    <span className='text-client-300 flex items-center gap-0.5 text-xs font-semibold'>
                      <img src={bolt} alt='' className='h-3 w-3' />+{t.priority_fee}
                    </span>
                  )}
                </button>
              ))}
              {deliveryTimes.length > 0 && (
                <p className='mt-2 flex items-center gap-1 text-xs italic text-slate-500'>
                  <img src={bolt} alt='' className='h-3 w-3' /> Fast delivery for an extra fee
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Continue button */}
      <div className='mt-4 flex justify-end'>
        <button
          type='button'
          className={`rounded-full px-6 py-2 text-sm font-semibold text-white transition ${
            !hasValid ? 'cursor-not-allowed bg-slate-300' : 'bg-client-300 hover:bg-client-400 shadow'
          }`}
          onClick={onContinue}
          disabled={!hasValid}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
